import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfigService } from '@app/core/config/config.service';
import { EmailTemplateType } from '@app/core/models/emails/email-template-type.model';
import { EmailTemplateModel } from '@app/core/models/emails/email-template.model';
import { ReferentialModel } from '@app/core/models/referentials/referential.model';
import { ReferentialsModel } from '@app/core/models/referentials/referentials.model';
import { EmailTemplateService } from '@app/core/services/email-template.service';
import { ModalComponent } from '@app/shared/components/modal/modal.component';
import { ModalHelper } from '@app/shared/models/modal-helper.model';
import { MultiSelectModel } from '@app/shared/models/multi-select.model';
import * as _ from 'lodash';
import { IDropdownSettings } from 'ng-multiselect-dropdown';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-email-template-detail',
  templateUrl: './email-template-detail.component.html',
  styleUrls: ['./email-template-detail.component.scss'],
})
export class EmailTemplateDetailComponent implements OnInit, OnDestroy {
  // Model business
  emailTemplateModel: EmailTemplateModel;
  emailTemplates: EmailTemplateModel[] = [];
  isUpdateMode = false;

  // model technique
  typeDropdownListData: ReferentialModel[] = [];
  typeSelectedItems = [];
  typeDropdownSettings: IDropdownSettings = {};

  // form
  emailTemplateForm: UntypedFormGroup;
  submitted = false;
  unsubscribe$: Subject<boolean> = new Subject<boolean>();
  emailTemplateContent: any = {};

  dynamicPortalUrlMarkerIsMandatory: boolean;
  dynamicAccessCodeMarkerIsMandatory: boolean;

  dynamicReasonsMarker: string;
  dynamicPortalUrlMarker: string;
  dynamicAccessCodeMarker: string;

  dynamicReasonsMarkerFound: boolean;
  dynamicPortalUrlMarkerFound: boolean;
  dynamicAccessCodeMarkerFound: boolean;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private emailTemplateService: EmailTemplateService,
    private formBuilder: UntypedFormBuilder,
    public dialog: MatDialog,
    private config: ConfigService,
    private multiSelectModel: MultiSelectModel
  ) {}

  get AreMarkersValid(): boolean {
    return (
      (!this.dynamicAccessCodeMarkerIsMandatory ||
        this.dynamicAccessCodeMarkerFound) &&
      (!this.dynamicPortalUrlMarkerIsMandatory ||
        this.dynamicPortalUrlMarkerFound)
    );
  }

  ngOnInit(): void {
    this.route.data.subscribe((data) => {
      this.emailTemplateModel = data?.emailTemplate;
      const referentials: ReferentialsModel = data.referentials;
      this.typeDropdownListData = referentials.EmailTemplateTypes.filter(
        (t) => t.Id != EmailTemplateType.All
      );
    });

    if (this.emailTemplateModel && this.emailTemplateModel.Id) {
      this.emailTemplateService
        .getEmailTemplateById(this.emailTemplateModel?.Id)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((emailTemplate: EmailTemplateModel) => {
          this.emailTemplateModel = emailTemplate;
        });
    }

    this.typeDropdownSettings = this.multiSelectModel.getSettings(
      'Id',
      'Label',
      true
    );
    this.isUpdateMode = !_.isNil(this.route.snapshot.paramMap.get('id'));
    this.dynamicReasonsMarker = this.config.environnement.dynamicReasonsMarker;
    this.dynamicAccessCodeMarker =
      this.config.environnement.dynamicAccessCodeMarker;
    this.dynamicPortalUrlMarker =
      this.config.environnement.dynamicPortalUrlMarker;

    this.buildFormControls();
    this.setContent();
    this.setTypeDropDownSelectedItems();
    this.updateMarkersStatus(this.emailTemplateModel.Content);
  }

  setContent() {
    if (!this.isUpdateMode) {
      this.emailTemplateModel = new EmailTemplateModel();
      this.emailTemplateModel.Content = this.dynamicReasonsMarker;
    }
  }

  setTypeDropDownSelectedItems() {
    this.typeSelectedItems = this.typeDropdownListData.filter(
      (x) => x.Id == this.emailTemplateModel?.TypeId
    );
    if (_.isNil(this.typeSelectedItems) || !this.typeSelectedItems.length) {
      this.updateMandatoryMarkers(null);
      return;
    }

    const typeIdSelected = _.head(this.typeSelectedItems)['Id'];
    this.updateMandatoryMarkers(typeIdSelected);
  }

  onSelectTemplateType(action: string): void {
    switch (action) {
      case 'select':
        const typeIdSelected = _.head(this.typeSelectedItems)['Id'];
        this.updateMandatoryMarkers(typeIdSelected);
        break;
      case 'deselect':
        this.updateMandatoryMarkers(null);
        break;
    }
  }

  updateMandatoryMarkers(typeIdSelected?: EmailTemplateType | null) {
    this.dynamicAccessCodeMarkerIsMandatory =
      !_.isNil(typeIdSelected) &&
      typeIdSelected === EmailTemplateType.ReturnToSupplier;
    this.dynamicPortalUrlMarkerIsMandatory =
      !_.isNil(typeIdSelected) &&
      typeIdSelected === EmailTemplateType.ReturnToSupplier;
  }

  onContentChanged(data): void {
    if (_.isNil(data.html)) {
      return;
    }
    this.emailTemplateModel.Content = data.html;
    this.updateMarkersStatus(data.html);
  }

  updateMarkersStatus(content: string) {
    this.dynamicReasonsMarkerFound =
      content.indexOf(this.dynamicReasonsMarker) >= 0;
    this.dynamicAccessCodeMarkerFound =
      content.indexOf(this.dynamicAccessCodeMarker) >= 0;
    this.dynamicPortalUrlMarkerFound =
      content.indexOf(this.dynamicPortalUrlMarker) >= 0;
  }

  buildFormControls() {
    this.emailTemplateForm = this.formBuilder.group({
      Title: [this.emailTemplateModel?.Title, Validators.required],
      Label: [this.emailTemplateModel?.Label, Validators.required],
      TypeId: [this.emailTemplateModel?.TypeId, Validators.required],
    });
  }

  setEmailTemplateModel() {
    const emailTemplateModel: EmailTemplateModel = Object.assign(
      {},
      this.emailTemplateModel
    );
    emailTemplateModel.TypeId = _.head(this.typeSelectedItems)['Id'];
    emailTemplateModel.TypeName = _.head(this.typeSelectedItems)['Label'];
    emailTemplateModel.Title = this.formControls.Title.value;
    emailTemplateModel.Label = this.formControls.Label.value;
    emailTemplateModel.Content = this.emailTemplateModel?.Content;

    this.isUpdateMode
      ? this.updateTemplate(this.emailTemplateModel.Id, emailTemplateModel)
      : this.createTemplate(emailTemplateModel);
  }

  updateTemplate(Id: number, emailTemplateModel: EmailTemplateModel) {
    this.emailTemplateService
      .updateEmailTemplate(Id, emailTemplateModel)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((_) => {
        this.returnBackToList();
      });
  }

  createTemplate(emailTemplateModel: EmailTemplateModel) {
    this.emailTemplateService
      .createEmailTemplate(emailTemplateModel)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((_) => {
        this.returnBackToList();
      });
  }

  onSubmit() {
    this.submitted = true;

    if (
      this.dynamicAccessCodeMarkerIsMandatory &&
      !this.dynamicAccessCodeMarkerFound
    ) {
      this.openMarkerMissingPopup(this.dynamicAccessCodeMarker);
      return;
    }

    if (
      this.dynamicPortalUrlMarkerIsMandatory &&
      !this.dynamicPortalUrlMarkerFound
    ) {
      this.openMarkerMissingPopup(this.dynamicPortalUrlMarker);
      return;
    }

    if (this.emailTemplateForm.valid) {
      this.onUpdate()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((isConfirmed) => {
          if (isConfirmed) {
            this.setEmailTemplateModel();
          }
        });
    }
  }

  onDelete() {
    const confimModal = new ModalHelper().getConfirmationConfig(
      `Êtes-vous sûr de vouloir supprimer ce template email ?`
    );
    const dialog = this.dialog.open(ModalComponent, confimModal);
    return dialog
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isConfirmed: boolean) => {
        if (isConfirmed) {
          return this.onDeleteConfirm();
        }
      });
  }

  onDeleteConfirm() {
    this.emailTemplateService
      .deleteEmailTemplate(this.emailTemplateModel.Id.toString())
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((_) => {
        this.returnBackToList();
      });
  }

  onUpdate(): Observable<any> {
    const confimModal = new ModalHelper().getConfirmationConfig(
      `Êtes-vous sûr de vouloir effectuer cette ${
        this.isUpdateMode ? 'modification' : 'création'
      } ?`
    );
    const dialog = this.dialog.open(ModalComponent, confimModal);
    return dialog.afterClosed().pipe(takeUntil(this.unsubscribe$));
  }

  get formControls(): { [key: string]: AbstractControl } {
    return this.emailTemplateForm.controls;
  }

  onCancel(): void {
    this.emailTemplateForm.reset();
    this.returnBackToList();
  }

  returnBackToList() {
    this.router.navigate(['administration/email-templates']);
  }

  openMarkerMissingPopup(marker: string) {
    const modalConfig = new ModalHelper().getAlertConfig(
      `Vous avez supprimé le marqueur <b>${marker}</b> permettant d'ajouter du contenu dynamique à votre email`
    );
    this.dialog.open(ModalComponent, modalConfig);
  }

  ngOnDestroy() {
    this.unsubscribe$.next(true);
  }
}
